import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { templatesApi } from '../api/index.js';
import { ClaudeHint } from '../components/ClaudeHint.js';

export function TemplateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [showPreview, setShowPreview] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['template', id],
    queryFn: () => templatesApi.get(id!),
    enabled: !!id,
  });

  if (isLoading) return <p>Loading…</p>;
  if (error) return <div className="alert alert-error">Failed to load template</div>;

  const t = data!.template;

  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <Link to="/templates" style={{ color: 'var(--color-text-muted)', fontSize: '.875rem' }}>← Templates</Link>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700 }}>{t.title}</h1>
          {t.description && <p style={{ color: 'var(--color-text-muted)' }}>{t.description}</p>}
        </div>
        <span className={`badge ${t.status === 'active' ? 'badge-green' : 'badge-gray'}`}>{t.status}</span>
      </div>

      {/* Fields */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <h2 style={{ fontWeight: 600, marginBottom: '1rem' }}>Fields ({t.fields.length})</h2>
        {t.fields.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>No fields defined.</p>
        ) : (
          <table>
            <thead>
              <tr><th>Name</th><th>Label</th><th>Type</th><th>Required</th></tr>
            </thead>
            <tbody>
              {t.fields.map((f) => (
                <tr key={f.name}>
                  <td><code>{f.name}</code></td>
                  <td>{f.label}</td>
                  <td>{f.type}</td>
                  <td>{f.required ? '✓' : '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Preview */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showPreview ? '1rem' : 0 }}>
          <h2 style={{ fontWeight: 600 }}>HTML Preview</h2>
          <div style={{ display: 'flex', gap: '.5rem' }}>
            <button
              className="btn btn-outline"
              onClick={() => setShowPreview((v) => !v)}
            >
              {showPreview ? 'Hide' : 'Show'}
            </button>
            <a href={templatesApi.htmlUrl(t.id)} target="_blank" rel="noreferrer" className="btn btn-outline">
              Open ↗
            </a>
          </div>
        </div>
        {showPreview && (
          <iframe
            src={templatesApi.htmlUrl(t.id)}
            title={`Preview: ${t.title}`}
            sandbox="allow-same-origin"
            style={{
              width: '100%',
              height: '520px',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius)',
              background: '#fff',
            }}
          />
        )}
      </div>

      {/* Claude hint */}
      <ClaudeHint
        title="Generate documents with Claude"
        prompt={`Use the generate-document-from-template skill to generate a document from the "${t.title}" template (id: ${t.id}). Fill in the required fields: ${t.fields.filter((f) => f.required).map((f) => f.name).join(', ')}.`}
      />
    </div>
  );
}
